// import { IBlogResponse, ISingleBlogResponse } from "@/types/blog.type";
import {
  IAppointmentPayload,
  IAppointmentStoreResponse,
  ICancelAppointmentPayload,
  IDoctorAvailabilityResponse,
  IDoctorInfoResponse,
  IErrorResponse,
  IVerificationPayload,
} from "@/types/agenda.type";
import { productionURL, staging_URL, AGENDA_HEADERS } from "./agenda-config";

export const BASE_URL = process.env.NEXT_PUBLIC_WEB_ENVIRONMENT === 'production' ? productionURL : staging_URL;

const handleResponse = async (response: Response) => {

  if (response.status == 204) {
    return true;
  }

  const data: any = await response.json();

  if (!response.ok) {
    if ("message" in data) {
      const errorMessage = data?.message;
      throw new Error(errorMessage);
    } else {
      throw new Error("Something went wrong");
    }
  }

  return data;
};

export const getDoctorInfo = (
  doctorId: string,
  department: string
): Promise<IDoctorInfoResponse> => {
  const decodedDoctorId = decodeURIComponent(doctorId);
  const decodedDepartment = decodeURIComponent(department);
  if (/[^a-zA-Z0-9._\u00C0-\u024F-]/.test(decodedDoctorId) || /[^a-zA-Z0-9_\u00C0-\u024F-]/.test(decodedDepartment)) {
    return Promise.reject(new Error("Invalid parameters"));
  }
  return fetch(`${BASE_URL}/guest/${encodeURIComponent(decodedDepartment)}/${encodeURIComponent(decodedDoctorId)}`, {
    ...AGENDA_HEADERS,
    method: "GET",
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then((data: IDoctorInfoResponse) => {
      return data;
    });
};

export const getAvailability = (
  doctorId: string,
  from: string,
  to: string,
  reasonDuration: number,
  future_search?: boolean
): Promise<IDoctorAvailabilityResponse> => {
  if (!/^\d+$/.test(doctorId)) {
    return Promise.reject(new Error("Invalid doctor ID"));
  }
  const params = new URLSearchParams({
    doctor_id: doctorId,
    from,
    to,
    visit_reason_duration: String(reasonDuration),
  });
  if (future_search) params.set('future_search', 'true');
  return fetch(
    `${BASE_URL}/guest/get_availability_v2?${params.toString()}`,
    { ...AGENDA_HEADERS, method: "GET" }
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then((data: IDoctorAvailabilityResponse) => {
      return data;
    });
};

export const sendAppointment = async (
  data: IAppointmentPayload
): Promise<IAppointmentStoreResponse | IErrorResponse> => {
  try {
    const response: Response = await fetch(
      `${BASE_URL}/guest/appointments/store`,
      { ...AGENDA_HEADERS, method: "POST", body: JSON.stringify(data) }
    );
    const responseData = await handleResponse(response);
    return responseData;
  } catch (error) {
    throw error;
  }
};

export const sendVerificationCode = async (
  data: IVerificationPayload,
  appointmentId: number
): Promise<boolean | IErrorResponse> => {
  try {
    const response: Response = await fetch(
      `${BASE_URL}/guest/appointments/${appointmentId}/status`,
      { ...AGENDA_HEADERS, method: "POST", body: JSON.stringify(data) }
    );
    const responseData = await handleResponse(response);
    return responseData;
  } catch (error) {
    throw error;
  }
};

export const resendVerificationCode = async (
  appointmentId: number
): Promise<boolean | IErrorResponse> => {
  try {
    const response: Response = await fetch(
      `${BASE_URL}/guest/appointments/${appointmentId}/resend-code`,
      { ...AGENDA_HEADERS, method: "POST" }
    );
    const responseData = await handleResponse(response);
    return responseData;
  } catch (error) {
    throw error;
  }
};


export const sendCancelAppointment = async (
  id: string,
  data: ICancelAppointmentPayload
): Promise<boolean | IErrorResponse> => {
  if (!/^\d+$/.test(id)) {
    throw new Error("Invalid appointment ID");
  }
  try {
    const response: Response = await fetch(
      `${BASE_URL}/guest/appointments/${encodeURIComponent(id)}/status`,
      { ...AGENDA_HEADERS, method: "POST", body: JSON.stringify(data) }
    );
    const responseData = await handleResponse(response);
    return responseData;
  } catch (error) {
    throw error;
  }
};

export const getDoctorProfiles = (): Promise<string[]> => {
  return fetch(`${BASE_URL}/guest/doctors-profiles`, {
    ...AGENDA_HEADERS,
    method: "GET",
    next: { revalidate: 60 * 60 * 24 * 7 }
  })
    .then((response) => {
      if (!response.ok) {
        return []
      }
      return response.json();
    })
    .then((data: string[]) => {
      return data;
    })
    .catch(()=>{
      return []
    });
};